import { useEffect } from "react";
import { FormActions } from "../../components/common/FormActions";
import {
  BooleanTags,
  SeverityTag,
} from "../../components/common/registerUtils";
import { IFormSection, TFormHookParams } from "../../types/common";
import {
  API_FRAUD_CONFIGURATIONS,
  API_FRAUD_RULE_TEMPLATES,
} from "../../utils/apis";
import {
  createViewEdit,
  formatDateTime,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_FRAUD_CONFIGURATIONS } from "../../utils/urls";
import useGet from "../common/useGet";
import { IFormSubmit } from "../common/useSet";

const url = URL_FRAUD_CONFIGURATIONS;

export const FRAUD_RULE_CATEGORIES: Record<string, string> = {
  // VELOCITY RULES
  VELOCITY_HIGH_FREQ_TX: "VELOCITY",
  VELOCITY_FAILED_TX: "VELOCITY",
  VELOCITY_MULTIPLE_BENEFICIARIES: "VELOCITY",
  // AMOUNT RULES
  AMOUNT_HIGH_VALUE_SPIKE: "AMOUNT",
  AMOUNT_THRESHOLD_EXCEEDED: "AMOUNT",
  AMOUNT_ROUND_NUMBER: "AMOUNT",
  // GEO RULES
  GEO_NEW_DEVICE: "GEO",
  GEO_IMPOSSIBLE_TRAVEL: "GEO",
  GEO_HIGH_RISK_COUNTRY: "GEO",
  // BEHAVIOR RULES
  BEHAVIOR_FAILED_LOGIN: "BEHAVIOR",
  BEHAVIOR_OFF_HOURS: "BEHAVIOR",
  BEHAVIOR_DORMANT_ACCOUNT: "BEHAVIOR",
  // PATTERN RULES
  PATTERN_STRUCTURED_TX: "PATTERN",
  PATTERN_CIRCULAR_TX: "PATTERN",
};

export const useFraudConfigurations = () => {
  const tableColumns = () => [
    { id: "template_code", header: "Code", filter: { type: "text" } },
    { id: "template_name", header: "Template Name", filter: { type: "text" } },
    { id: "category", header: "Category", filter: { type: "text" } },
    { id: "name", header: "Name", filter: { type: "text" } },
    { id: "description", header: "Description", filter: { type: "text" } },
    { id: "score_weight", header: "Score Weight", filter: { type: "number" } },
    { id: "severity", header: "Severity", filter: { type: "text" } },
    { id: "action_type", header: "Action Type", filter: { type: "text" } },
    { id: "is_active", header: "Is Active", filter: { type: "boolean" } },
    { id: "created_at", header: "Created At" },
    {
      id: "trigger_count",
      header: "Trigger Count",
      filter: { type: "number" },
    },
    {
      id: "last_triggered",
      header: "Last Triggered",
      filter: { type: "date" },
    },
    { id: "actions", header: "Actions" },
  ];

  const tableData = (data: any /*, reloadData?: any*/) => [
    ...data?.map((item: any) => {
      return {
        ...item,
        template_code: item?.template_code || "N/A",
        template_name: item?.template_name || "N/A",
        category: item?.category || "N/A",
        name: item?.name || "N/A",
        description: item?.description || "N/A",
        score_weight: item?.score_weight || 0,
        severity: <SeverityTag severity={item?.severity} />,
        action_type: item?.action_type || "N/A",
        is_active: <BooleanTags status={item?.is_active} />,
        created_at: item?.created_at ? formatDateTime(item?.created_at) : "N/A",
        trigger_count: item?.trigger_count || 0,
        last_triggered: item?.last_triggered
          ? formatDateTime(item?.last_triggered)
          : "Never",
        actions: (
          <FormActions
            actions={[
              ...createViewEdit(url, item?.id, true),

              // {
              //   label: "Edit",
              //   onClick: () =>
              //     openModalForm({
              //       source_id: "fraud_configurations",
              //       id: item?.id,
              //       reloadData,
              //     }),
              // },
            ]}
          />
        ),
      };
    }),
  ];

  const viewData = (item: any) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: {
              message: "No data available",
            },
          },
        ],
      };
    }
    return {
      sections: [
        {
          title: "Configuration Details",
          fields: {
            template_code: item?.template_code,
            template_name: item?.template_name,
            category: item?.category,
            name: item?.name,
            description: item?.description,
            score_weight: item?.score_weight,
            severity: <SeverityTag severity={item?.severity} />,
            action_type: item?.action_type,
            is_active: <BooleanTags status={item?.is_active} />,
            created_at: item?.created_at
              ? formatDateTime(item?.created_at)
              : "N/A",
            trigger_count: item?.trigger_count,
            last_triggered: item?.last_triggered
              ? formatDateTime(item?.last_triggered)
              : "Never",
          },
        },
        {
          title: "User Type Restrictions",
          fields: {
            applies_to_user_types:
              item?.applies_to_user_types?.join(", ") || "All",
          },
        },
        {
          title: "Parameter Values",
          fields: item?.parameter_values?.reduce((acc: any, param: any) => {
            acc[param.parameter_name] =
              `${param.value} (${param.parameter_type})`;
            return acc;
          }, {}) || { message: "No parameters configured" },
        },
        {
          title: "Validation Status",
          fields: {
            is_valid: <BooleanTags status={item?.validation?.is_valid} />,
            errors: item?.validation?.errors
              ? JSON.stringify(item?.validation?.errors)
              : "No errors",
          },
        },
        {
          title: "Created By",
          fields: {
            name:
              item?.created_by?.first_name + " " + item?.created_by?.last_name,
            email: item?.created_by?.email,
            phone_number: item?.created_by?.phone_number,
            role: item?.created_by?.role,
          },
        },
      ],
    };
  };

  const formRoute = getForm(url);

  return {
    tableColumns,
    tableData,
    viewData,
    formRoute,
  };
};

export const useFraudConfigurationsForm = ({
  form,
  updateFormValues,
  id,
}: TFormHookParams) => {
  const { data: templates, loadData: loadTemplates } = useGet();
  const { parameter_values } = form || {};

  const category = form?.category;
  const templateId = form?.template;

  useEffect(() => {
    if (category) {
      loadTemplates({
        api: `${API_FRAUD_RULE_TEMPLATES}?category=${category}`,
      });
    }
  }, [category]);

  // Get unique categories from FRAUD_RULE_CATEGORIES
  const categoryOptions = Array.from(
    new Set(Object.values(FRAUD_RULE_CATEGORIES)),
  ).map((cat) => ({
    id: cat.toLowerCase(),
    name: cat,
  }));

  // Map templates to options
  const templateOptions =
    templates?.results?.map((template: any) => ({
      id: template.id,
      name: `${template.code} - ${template.name}`,
    })) || [];

  // Get selected template's parameters
  const selectedTemplate = templates?.results?.find(
    (t: any) => t.id === templateId,
  );
  const templateParameters = selectedTemplate?.parameters || [];

  useEffect(() => {
    if (id && parameter_values?.length > 0 && templateParameters?.length > 0) {
      const parameters = {};
      parameter_values.forEach((parameter_value: any) => {
        const parameter = parameter_value?.value;
        const parameter_name = templateParameters?.find(
          (p: any) => p?.parameter_name === parameter_value?.parameter_name,
        )?.parameter_name;
        parameters[parameter_name] = parameter;
      });
      updateFormValues({ parameters });
    }
  }, [parameter_values, templateParameters]);

  const sections: IFormSection[] = [
    {
      title: "Configuration Details",
      fields: [
        {
          field_name: "category",
          type: "select",
          label: "Category",
          props: {
            required: true,
            placeholder: "Select a category",
          },
          options_data: categoryOptions,
        },
        {
          field_name: "template",
          type: "select",
          label: "Template",
          props: {
            required: true,
            placeholder: "Select a template",
            disabled: !category,
          },
          options_data: templateOptions,
        },
        {
          field_name: "name",
          label: "Configuration Name",
          props: {
            required: true,
            placeholder: "e.g., High Value Spike - Agents-test-workflow-08",
          },
        },
        {
          field_name: "description",
          type: "textarea",
          label: "Description",
          props: {
            required: true,
            placeholder: "e.g., Flag 3x spikes",
          },
        },
      ],
    },
    {
      title: "Risk Settings",
      fields: [
        {
          field_name: "score_weight",
          label: "Score Weight",
          props: {
            required: true,
            placeholder: "e.g., 40",
            type: "number",
            min: 0,
            max: 100,
          },
        },
        {
          field_name: "severity",
          type: "select",
          label: "Severity",
          props: {
            required: true,
            placeholder: "Select severity",
          },
          options_data: [
            { id: "LOW", name: "Low" },
            { id: "MEDIUM", name: "Medium" },
            { id: "HIGH", name: "High" },
            { id: "CRITICAL", name: "Critical" },
          ],
        },
        {
          field_name: "action_type",
          type: "select",
          label: "Action Type",
          props: {
            required: true,
            placeholder: "Select action type",
          },
          options_data: [
            { id: "REVIEW", name: "Review" },
            { id: "BLOCK", name: "Block" },
            { id: "ALERT", name: "Alert" },
            { id: "LOG", name: "Log Only" },
          ],
        },
      ],
    },
    {
      title: "User Type Restrictions",
      fields: [
        {
          field_name: "applies_to_user_types",
          type: "select",
          label: "Applies To User Types",
          props: {
            required: true,
            placeholder: "Select user types",
            mode: "multiple",
          },
          options_data: [
            { id: "INTERNAL", name: "Internal" },
            { id: "EXTERNAL", name: "External" },
            { id: "MERCHANT", name: "Merchant" },
            { id: "AGENT", name: "Agent" },
            { id: "CUSTOMER", name: "Customer" },
          ],
        },
      ],
    },
    {
      title: "Parameters",
      fields: [
        {
          field_name: "parameters",
          type: "multi_number_input",
          label: "Template Parameters",
          props: {
            parameters: templateParameters,
            required: templateParameters.some((p: any) => p.is_required),
          },
        },
      ],
    },
  ];

  const redirect = getRegister(url);

  return { sections, redirect };
};

export const submitFraudConfigurations = (
  payload: any,
  id: string,
  _: any,
  reloadData: any,
): IFormSubmit => {
  // Clean up payload to match expected format
  const cleanPayload = {
    template: payload.template,
    name: payload.name,
    description: payload.description,
    score_weight: parseInt(payload.score_weight),
    severity: payload.severity,
    action_type: payload.action_type,
    applies_to_user_types: payload.applies_to_user_types,
    parameters: payload.parameters || {},
  };

  if (id) {
    return {
      id,
      api: API_FRAUD_CONFIGURATIONS,
      params: cleanPayload,
      onSuccess: () => {
        if (reloadData) reloadData({});
      },
    };
  }
  return {
    api: API_FRAUD_CONFIGURATIONS,
    params: cleanPayload,
  };
};
